package com.neurobs.js

import com.neurobs.ccab.{GeneralResult, ObjectResult}
import io.circe.Decoder
import org.scalajs.dom._
import org.scalajs.dom.ext.Ajax
import org.scalajs.dom.raw.{ProgressEvent, XMLHttpRequest}

import scala.concurrent.ExecutionContext.Implicits.global
import scala.concurrent.{Future, Promise}
import scala.util.{Failure, Success}
import scala.util.control.NonFatal
import com.neurobs.js.view.Form

import scala.scalajs.js.URIUtils

package object http {
	import scala.language.implicitConversions

	var onUnauthorizedError: () => Unit = () => Unit

	object HTTPRequestType extends Enumeration {
		val Get    = Value("GET")
		val Post   = Value("POST")
		val Delete = Value("DELETE")
		val Put    = Value("PUT")
	}

	/** Error message and code describing a failed interaction with a server,
	  * including communication failure, server error, or error interpreting the
	  * response data.
	  *
	  * @param code If a response was received, this is the HTTP status
	  *             code of the response. If an error prevented any valid HTTP response,
	  *             this will be 0. Note that the code may be httpStatusOK if there was
	  *             some error processing the data in the response.
	  * @param message Error message
	  */
	case class ServerError(code: Int, message: String)

	val httpStatusOK = 200

	/** Result of a request to the server that is either an error, or
	  * the body of the response as a string
	  */
	type ServerResponse = Either[ServerError, String]

	type ServerObjectResponse[T] = Either[ServerError, T]

	/** Generates an appropriate error based on the HTTP status code of a response
	  *
	  * @param r The request which must be completed
	  * @return Error information, or None if the response had status code httpStatusOK
	  */
	private def checkResponseCode(r: XMLHttpRequest): Option[ServerError] = {
		val message = r.status match {
			case 0 => "Unable to contact the server."
			case 200 => ""
			case 401 =>
				onUnauthorizedError()
				"The user must be logged in."
			case 403 => "The user does not have permissions to access the requested item."
			case 404 => "The requested object was not found in the server data."
			case c => s"A server error with response code $c occurred."
		}
		if (message.isEmpty) None else Some(ServerError(r.status, message))
	}

	/** Interface to a response in process
	  */
	trait RequestProcess {
		/** Aborts the request if possible */
		def abort() : Unit

		/** Sets handler called on progress updates
		  *
		  * @param onProgress Callback taking the fraction complete
		  */
		def setOnProgress(onProgress: (Double) => Unit) : Unit
	}

	implicit def requestFormProcessor(r: RequestProcess): Form.Process = new Form.Process {
		override def cancel(): Unit = r.abort()
	}

	/** Implementation of RequestProcess using an XMLHttpRequest */
	private class XMLRequestProcess(req: XMLHttpRequest, promise: Promise[XMLHttpRequest]) extends RequestProcess {
		private var onProgress: Option[(Double) => Unit] = None
		def abort() : Unit = {
			if (!promise.isCompleted) {
				promise.failure(new Exception(""))
				req.abort
			}
		}
		def setOnProgress(fun: (Double) => Unit) : Unit = { onProgress = Some(fun) }
		req.upload.addEventListener("progress", (e: ProgressEvent) => {
			onProgress.map { f =>
				val total = e.total
				val percent = if (total != 0) e.loaded / total else 0
				f(percent) }
		}, false)
	}

	/** Initiates an HTTP request
	  *
	  * @param url URL for the request
	  * @param t type of request
	  * @param data Data for the body of the request
	  * @return Process and Future result of the request
	  */
	def makeServerCall(url: String, t: HTTPRequestType.Value, data: Ajax.InputData) : (RequestProcess, Future[ServerResponse]) = {
		val req = new XMLHttpRequest()
		// Need to create this here to make sure hookup of upload progress handler
		// is done before call to open
		val promise = Promise[XMLHttpRequest]()
		val requestProcess = new XMLRequestProcess(req, promise)

		try {
			req.onreadystatechange = (e: Event) => {
				if (req.readyState == 4) {
					if (!promise.isCompleted) {
						promise.success(req)
					}
				}
			}
			req.open(t.toString, url)
			req.setRequestHeader("Csrf-Token", "nocheck")
			req.responseType = ""
			req.timeout = 0
			req.withCredentials = false
			if (data == null) {
				req.send()
			}
			else {
				req.send(data)
			}
		}
		catch {
			case e: Throwable => promise.failure(e)
		}

		val res = promise.future map { r =>
			checkResponseCode(r) match {
				case None => Right(r.responseText)
				case Some(e) => Left(e)
			}
		}
		(requestProcess, res)
	}

	/** Initiates an HTTP request expected to return JSON for the type T in the
	  * body of the response.
	  *
	  * @param url URL for the request
	  * @param t Type of request
	  * @param data Data for the body of the request
	  * @param d JSON decoder of the returned type
	  * @tparam T Type of object returned by the server as JSON
	  * @return Process and Future result of the request
	  */
	def getServerObject[T](url: String, t: HTTPRequestType.Value, data: Ajax.InputData)(implicit d: Decoder[T]): (RequestProcess, Future[Either[ServerError,T]]) = {
		val temp = makeServerCall(url, t, data)
		val obj = temp._2 map {
			case Left(e) => Left(e)
			case Right(s) => io.circe.parser.decode[T](s) match {
				case Left(e) => Left(ServerError(200, "The data sent by the server was invalid."))
				case Right(obj) => Right(obj)
			}
		}
		(temp._1, obj)
	}

	/** Attaches a callback function to a future response.
	  *
	  * @param result Value from the getServerObject method
	  * @param onComplete Callback called when the Future completes
	  * @tparam T Type of object returned as JSON
	  * @return Process from the result argument
	  */
	def serverCallAsync[T](result: (RequestProcess, Future[Either[ServerError,T]]))(onComplete: (Either[ServerError,T]) => Unit): RequestProcess = {
		result._2.onComplete {
			case Success(r) => onComplete(r)
			case Failure(f) => f match {
				case NonFatal(e) => onComplete(Left(ServerError(0, e.getLocalizedMessage())))
				case _ => throw f
			}
		}
		result._1
	}

	/** Initiates a server call expected to return no data in the body, handled by
	  * a callback.
	  *
	  * @param url URL of the request
	  * @param t Type of the request
	  * @param data Data for the body of the request
	  * @param onComplete Callback taking error information if any
	  * @return Process for the request
	  */
	def serverCallAsyncNoResponse(url: String, t: HTTPRequestType.Value, data: Ajax.InputData = null)(onComplete: Option[ServerError] => Unit) : RequestProcess = {
		val temp = makeServerCall(url, t, data)
		temp._2.onComplete {
			case Success(t) => t match {
				case Left(e) => onComplete(Some(e))
				case Right(_) => onComplete(None)
			}
			case Failure(f) => f match {
				case NonFatal(e) => onComplete(Some(ServerError(0, e.getLocalizedMessage())))
				case _ => throw f
			}
		}
		temp._1
	}

	/** Initiates a server call expected to return JSON for an object in the body, handled
	  * by a callback.
	  *
	  * @param url URL of the request
	  * @param t Type of the request
	  * @param data Data for the body of the request
	  * @param onComplete Callback taking either an error of an object
	  * @param d JSON decoder of the returned type
	  * @tparam T Type of object returned by the server as JSON
	  * @return Process for the request
	  */
	def getServerObjectAsync[T](url: String, t: HTTPRequestType.Value, data: Ajax.InputData = null)(onComplete: (Either[ServerError,T]) => Unit)(implicit d: Decoder[T]) : RequestProcess = {
		serverCallAsync[T](getServerObject[T](url, t, data))(onComplete)
	}

	def urlEncode(s: String) : String = {
		URIUtils.encodeURI(s)
		//s.getBytes("UTF-8").map(c => "%" + ("%02X" format c)).mkString
	}

	def typeHandler[T](errorCallback: Option[String] => Unit)(handler: T => Unit)(response: Either[ServerError,T]): Unit = {
		response match {
			case Left(error) => errorCallback(Some(error.message))
			case Right(value) => handler(value)
		}
	}

	def generalHandler(errorCallback: Option[String] => Unit)(handler: () => Unit)(response: Either[ServerError, GeneralResult]): Unit = {
		response match {
			case Left(error) => errorCallback(Some(error.message))
			case Right(value) =>value.error match {
				case Some(error) => errorCallback(Some(error))
				case None => handler()
			}
		}
	}

	def objectHandler[T](errorCallback: Option[String] => Unit)(handler: T => Unit)(response: Either[ServerError, ObjectResult[T]]): Unit = {
		response match {
			case Left(error) => errorCallback(Some(error.message))
			case Right(value) =>value.error match {
				case Some(error) => errorCallback(Some(error))
				case None => value.data match {
					case None => errorCallback(Some("Object response was empty."))
					case Some(data) => handler(data)
				}
			}
		}
	}
}
